import { useState } from "react";
import {
  Modal,
  Platform,
  Pressable,
  ScrollView,
  Text,
  View,
} from "react-native";
import { Eye, X } from "lucide-react-native";
import { SectionTitle } from "@louise/moon-ui";
import { GLASS_CARD_STYLE, EMBER } from "@louise/moon-ui/theme/tokens";
import Svg, { Circle, Ellipse, G, Line, Path, Polygon } from "react-native-svg";
import { useTranslations } from "@src/packages/i18n";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import { HorizontalSnapCarousel } from "./HorizontalSnapCarousel";

export type BiologyVisualKind =
  | "normal-cycle"
  | "follicle-cohort"
  | "medication-signal"
  | "monitoring"
  | "trigger"
  | "retrieval"
  | "reserve"
  | "quality"
  | "freezing"
  | "maturity"
  | "attrition";

type BiologySlide = {
  id: string;
  visual: BiologyVisualKind;
  label: string;
  title: string;
  body: string;
  reserveNote: string;
  detail: {
    title: string;
    sections: { title: string; body: string }[];
  };
};

function useBiologySlides(): BiologySlide[] {
  const t = useTranslations();
  return [
    {
      id: "normal-cycle",
      visual: "normal-cycle",
      label: t("homepage.egg_freezing_biology_carousel.normal_cycle_label"),
      title: t("homepage.egg_freezing_biology_carousel.normal_cycle_title"),
      body: t("homepage.egg_freezing_biology_carousel.normal_cycle_body"),
      reserveNote: t(
        "homepage.egg_freezing_biology_carousel.normal_cycle_reserve_note",
      ),
      detail: {
        title: t(
          "homepage.egg_freezing_biology_carousel.normal_cycle_detail_title",
        ),
        sections: [
          {
            title: t(
              "homepage.egg_freezing_biology_carousel.normal_cycle_section_1_title",
            ),
            body: t(
              "homepage.egg_freezing_biology_carousel.normal_cycle_section_1_body",
            ),
          },
          {
            title: t(
              "homepage.egg_freezing_biology_carousel.normal_cycle_section_2_title",
            ),
            body: t(
              "homepage.egg_freezing_biology_carousel.normal_cycle_section_2_body",
            ),
          },
          {
            title: t(
              "homepage.egg_freezing_biology_carousel.normal_cycle_section_3_title",
            ),
            body: t(
              "homepage.egg_freezing_biology_carousel.normal_cycle_section_3_body",
            ),
          },
        ],
      },
    },
    {
      id: "cohort",
      visual: "follicle-cohort",
      label: t("homepage.shared.stimulation"),
      title: t("homepage.egg_freezing_biology_carousel.cohort_title"),
      body: t("homepage.egg_freezing_biology_carousel.cohort_body"),
      reserveNote: t(
        "homepage.egg_freezing_biology_carousel.cohort_reserve_note",
      ),
      detail: {
        title: t("homepage.egg_freezing_biology_carousel.cohort_detail_title"),
        sections: [
          {
            title: t(
              "homepage.egg_freezing_biology_carousel.cohort_section_1_title",
            ),
            body: t(
              "homepage.egg_freezing_biology_carousel.cohort_section_1_body",
            ),
          },
          {
            title: t(
              "homepage.egg_freezing_biology_carousel.cohort_section_2_title",
            ),
            body: t(
              "homepage.egg_freezing_biology_carousel.cohort_section_2_body",
            ),
          },
          {
            title: t(
              "homepage.egg_freezing_biology_carousel.cohort_section_3_title",
            ),
            body: t(
              "homepage.egg_freezing_biology_carousel.cohort_section_3_body",
            ),
          },
        ],
      },
    },
    {
      id: "medication",
      visual: "medication-signal",
      label: t("homepage.egg_freezing_biology_carousel.daily_meds_label"),
      title: t("homepage.egg_freezing_biology_carousel.medication_title"),
      body: t("homepage.egg_freezing_biology_carousel.medication_body"),
      reserveNote: t(
        "homepage.egg_freezing_biology_carousel.medication_reserve_note",
      ),
      detail: {
        title: t(
          "homepage.egg_freezing_biology_carousel.medication_detail_title",
        ),
        sections: [
          {
            title: t(
              "homepage.egg_freezing_biology_carousel.medication_section_1_title",
            ),
            body: t(
              "homepage.egg_freezing_biology_carousel.medication_section_1_body",
            ),
          },
          {
            title: t(
              "homepage.egg_freezing_biology_carousel.medication_section_2_title",
            ),
            body: t(
              "homepage.egg_freezing_biology_carousel.medication_section_2_body",
            ),
          },
          {
            title: t(
              "homepage.egg_freezing_biology_carousel.medication_section_3_title",
            ),
            body: t(
              "homepage.egg_freezing_biology_carousel.medication_section_3_body",
            ),
          },
        ],
      },
    },
    {
      id: "monitoring",
      visual: "monitoring",
      label: t("homepage.egg_freezing_biology_carousel.monitoring_label"),
      title: t("homepage.egg_freezing_biology_carousel.monitoring_title"),
      body: t("homepage.egg_freezing_biology_carousel.monitoring_body"),
      reserveNote: t(
        "homepage.egg_freezing_biology_carousel.monitoring_reserve_note",
      ),
      detail: {
        title: t(
          "homepage.egg_freezing_biology_carousel.monitoring_detail_title",
        ),
        sections: [
          {
            title: t(
              "homepage.egg_freezing_biology_carousel.monitoring_section_1_title",
            ),
            body: t(
              "homepage.egg_freezing_biology_carousel.monitoring_section_1_body",
            ),
          },
          {
            title: t(
              "homepage.egg_freezing_biology_carousel.monitoring_section_2_title",
            ),
            body: t(
              "homepage.egg_freezing_biology_carousel.monitoring_section_2_body",
            ),
          },
          {
            title: t(
              "homepage.egg_freezing_biology_carousel.monitoring_section_3_title",
            ),
            body: t(
              "homepage.egg_freezing_biology_carousel.monitoring_section_3_body",
            ),
          },
        ],
      },
    },
    {
      id: "trigger",
      visual: "trigger",
      label: t("homepage.egg_freezing_biology_carousel.trigger_label"),
      title: t("homepage.egg_freezing_biology_carousel.trigger_title"),
      body: t("homepage.egg_freezing_biology_carousel.trigger_body"),
      reserveNote: t(
        "homepage.egg_freezing_biology_carousel.trigger_reserve_note",
      ),
      detail: {
        title: t("homepage.egg_freezing_biology_carousel.trigger_detail_title"),
        sections: [
          {
            title: t(
              "homepage.egg_freezing_biology_carousel.trigger_section_1_title",
            ),
            body: t(
              "homepage.egg_freezing_biology_carousel.trigger_section_1_body",
            ),
          },
          {
            title: t(
              "homepage.egg_freezing_biology_carousel.trigger_section_2_title",
            ),
            body: t(
              "homepage.egg_freezing_biology_carousel.trigger_section_2_body",
            ),
          },
          {
            title: t(
              "homepage.egg_freezing_biology_carousel.trigger_section_3_title",
            ),
            body: t(
              "homepage.egg_freezing_biology_carousel.trigger_section_3_body",
            ),
          },
        ],
      },
    },
    {
      id: "retrieval",
      visual: "retrieval",
      label: t("homepage.egg_freezing_biology_carousel.retrieval_label"),
      title: t("homepage.egg_freezing_biology_carousel.retrieval_title"),
      body: t("homepage.egg_freezing_biology_carousel.retrieval_body"),
      reserveNote: t(
        "homepage.egg_freezing_biology_carousel.retrieval_reserve_note",
      ),
      detail: {
        title: t(
          "homepage.egg_freezing_biology_carousel.retrieval_detail_title",
        ),
        sections: [
          {
            title: t(
              "homepage.egg_freezing_biology_carousel.retrieval_section_1_title",
            ),
            body: t(
              "homepage.egg_freezing_biology_carousel.retrieval_section_1_body",
            ),
          },
          {
            title: t(
              "homepage.egg_freezing_biology_carousel.retrieval_section_2_title",
            ),
            body: t(
              "homepage.egg_freezing_biology_carousel.retrieval_section_2_body",
            ),
          },
          {
            title: t(
              "homepage.egg_freezing_biology_carousel.retrieval_section_3_title",
            ),
            body: t(
              "homepage.egg_freezing_biology_carousel.retrieval_section_3_body",
            ),
          },
        ],
      },
    },
    {
      id: "reserve",
      visual: "reserve",
      label: t("homepage.egg_freezing_biology_carousel.reserve_label"),
      title: t("homepage.egg_freezing_biology_carousel.reserve_title"),
      body: t("homepage.egg_freezing_biology_carousel.reserve_body"),
      reserveNote: t(
        "homepage.egg_freezing_biology_carousel.reserve_reserve_note",
      ),
      detail: {
        title: t("homepage.egg_freezing_biology_carousel.reserve_detail_title"),
        sections: [
          {
            title: t(
              "homepage.egg_freezing_biology_carousel.reserve_section_1_title",
            ),
            body: t(
              "homepage.egg_freezing_biology_carousel.reserve_section_1_body",
            ),
          },
          {
            title: t(
              "homepage.egg_freezing_biology_carousel.reserve_section_2_title",
            ),
            body: t(
              "homepage.egg_freezing_biology_carousel.reserve_section_2_body",
            ),
          },
          {
            title: t(
              "homepage.egg_freezing_biology_carousel.reserve_section_3_title",
            ),
            body: t(
              "homepage.egg_freezing_biology_carousel.reserve_section_3_body",
            ),
          },
        ],
      },
    },
    {
      id: "freezing",
      visual: "freezing",
      label: t("homepage.shared.freezing"),
      title: t("homepage.egg_freezing_biology_carousel.freezing_title"),
      body: t("homepage.egg_freezing_biology_carousel.freezing_body"),
      reserveNote: t(
        "homepage.egg_freezing_biology_carousel.freezing_reserve_note",
      ),
      detail: {
        title: t(
          "homepage.egg_freezing_biology_carousel.freezing_detail_title",
        ),
        sections: [
          {
            title: t(
              "homepage.egg_freezing_biology_carousel.freezing_section_1_title",
            ),
            body: t(
              "homepage.egg_freezing_biology_carousel.freezing_section_1_body",
            ),
          },
          {
            title: t(
              "homepage.egg_freezing_biology_carousel.freezing_section_2_title",
            ),
            body: t(
              "homepage.egg_freezing_biology_carousel.freezing_section_2_body",
            ),
          },
          {
            title: t(
              "homepage.egg_freezing_biology_carousel.freezing_section_3_title",
            ),
            body: t(
              "homepage.egg_freezing_biology_carousel.freezing_section_3_body",
            ),
          },
        ],
      },
    },
  ];
}

export function BiologyIllustration({ kind }: { kind: BiologyVisualKind }) {
  const ink = "#4A302C";
  const softInk = "rgba(74, 48, 44, 0.22)";
  const clay = "#9B5E50";
  const mauve = "#7C5961";
  const apricot = "#D4946A";
  const blush = "#E6B190";
  const fill = "rgba(255,244,236,0.54)";
  const clayWash = "rgba(155,94,80,0.1)";
  const mauveWash = "rgba(124,89,97,0.1)";
  const apricotWash = "rgba(212,148,106,0.14)";

  return (
    <View
      style={{
        alignSelf: "center",
        width: 174,
        height: 118,
        alignItems: "center",
        justifyContent: "center",
        marginTop: 6,
        marginBottom: 12,
      }}
    >
      {kind === "normal-cycle" ? (
        <Svg width={174} height={118} viewBox="0 0 174 118" fill="none">
          <Ellipse cx={88} cy={62} rx={58} ry={38} fill={clayWash} />
          {[48, 66, 85, 105].map((cx, i) => (
            <Circle
              key={cx}
              cx={cx}
              cy={[68, 49, 73, 58][i]}
              r={[11, 13, 10, 24][i]}
              fill={fill}
              stroke={i === 3 ? clay : softInk}
              strokeWidth={i === 3 ? 2.4 : 1.5}
            />
          ))}
          <Circle cx={105} cy={58} r={7} stroke={ink} strokeWidth={1.6} />
          <Path
            d="M126 55C141 55 148 44 151 32"
            stroke={clay}
            strokeWidth={2}
            strokeLinecap="round"
            strokeDasharray="4 5"
          />
        </Svg>
      ) : null}

      {kind === "follicle-cohort" ? (
        <Svg width={174} height={118} viewBox="0 0 174 118" fill="none">
          <Ellipse cx={86} cy={62} rx={60} ry={40} fill={mauveWash} />
          {[52, 74, 96, 119].map((cx, i) => (
            <G key={cx}>
              <Circle
                cx={cx}
                cy={[66, 48, 70, 52][i]}
                r={[16, 20, 17, 14][i]}
                fill={fill}
                stroke={mauve}
                strokeWidth={2}
              />
              <Circle
                cx={cx}
                cy={[66, 48, 70, 52][i]}
                r={[5, 6, 5, 4][i]}
                stroke={ink}
                strokeWidth={1.4}
              />
            </G>
          ))}
          <Path
            d="M36 28C56 18 111 17 134 30"
            stroke={softInk}
            strokeWidth={2}
            strokeLinecap="round"
          />
        </Svg>
      ) : null}

      {kind === "medication-signal" ? (
        <Svg width={174} height={118} viewBox="0 0 174 118" fill="none">
          <Path
            d="M38 77C53 44 78 43 91 68C103 91 125 83 139 48"
            stroke={apricot}
            strokeWidth={3}
            strokeLinecap="round"
          />
          {[45, 72, 94, 119, 137].map((cx, i) => (
            <Circle
              key={cx}
              cx={cx}
              cy={[64, 47, 72, 75, 50][i]}
              r={9}
              fill={fill}
              stroke={clay}
              strokeWidth={1.6}
            />
          ))}
          <Line
            x1={35}
            y1={92}
            x2={141}
            y2={92}
            stroke={softInk}
            strokeWidth={1.5}
            strokeLinecap="round"
          />
          <Path
            d="M51 27H78L72 38H57L51 27Z"
            stroke={ink}
            strokeWidth={1.6}
            strokeLinejoin="round"
          />
        </Svg>
      ) : null}

      {kind === "monitoring" ? (
        <Svg width={174} height={118} viewBox="0 0 174 118" fill="none">
          <Path
            d="M38 84H137"
            stroke={softInk}
            strokeWidth={1.5}
            strokeLinecap="round"
          />
          <Path
            d="M42 80C52 76 58 64 68 62C82 59 83 73 96 67C110 60 113 39 135 35"
            stroke={mauve}
            strokeWidth={3}
            strokeLinecap="round"
          />
          {[46, 68, 95, 118, 135].map((cx, i) => (
            <Circle
              key={cx}
              cx={cx}
              cy={[78, 62, 67, 45, 35][i]}
              r={4}
              fill="#fff"
              stroke={ink}
              strokeWidth={1.4}
            />
          ))}
          <Circle cx={57} cy={33} r={13} fill={fill} stroke={clay} />
          <Circle cx={57} cy={33} r={4} stroke={ink} strokeWidth={1.2} />
        </Svg>
      ) : null}

      {kind === "trigger" ? (
        <Svg width={174} height={118} viewBox="0 0 174 118" fill="none">
          <Circle cx={86} cy={61} r={34} fill={apricotWash} />
          <Circle
            cx={86}
            cy={61}
            r={25}
            fill={fill}
            stroke={apricot}
            strokeWidth={2.4}
          />
          <Circle cx={86} cy={61} r={8} stroke={ink} strokeWidth={1.6} />
          <Path
            d="M124 37L136 49L124 61L112 49L124 37Z"
            fill={fill}
            stroke={clay}
            strokeWidth={1.6}
          />
          <Line
            x1={124}
            y1={68}
            x2={124}
            y2={87}
            stroke={ink}
            strokeWidth={1.5}
          />
          <Line
            x1={116}
            y1={87}
            x2={132}
            y2={87}
            stroke={ink}
            strokeWidth={1.5}
          />
        </Svg>
      ) : null}

      {kind === "retrieval" ? (
        <Svg width={174} height={118} viewBox="0 0 174 118" fill="none">
          <Ellipse cx={76} cy={64} rx={38} ry={28} fill={clayWash} />
          <Circle
            cx={66}
            cy={58}
            r={17}
            fill={fill}
            stroke={clay}
            strokeWidth={2}
          />
          <Circle
            cx={88}
            cy={72}
            r={14}
            fill={fill}
            stroke={clay}
            strokeWidth={1.8}
          />
          <Path
            d="M129 34L91 62"
            stroke={ink}
            strokeWidth={2}
            strokeLinecap="round"
          />
          <Path
            d="M126 31L138 43"
            stroke={ink}
            strokeWidth={2}
            strokeLinecap="round"
          />
          <Circle cx={91} cy={62} r={4} fill={blush} />
        </Svg>
      ) : null}

      {kind === "reserve" ? (
        <Svg width={174} height={118} viewBox="0 0 174 118" fill="none">
          <Path
            d="M36 64C50 41 75 35 89 57C102 77 126 75 140 53"
            stroke={softInk}
            strokeWidth={2}
            strokeLinecap="round"
          />
          {[48, 61, 75, 91, 107, 123, 137].map((cx, i) => (
            <Circle
              key={cx}
              cx={cx}
              cy={[67, 53, 45, 58, 70, 67, 54][i]}
              r={i < 3 ? 8 : 6}
              fill={fill}
              stroke={i < 3 ? clay : softInk}
              strokeWidth={1.5}
            />
          ))}
          <Path
            d="M44 91H132"
            stroke={mauve}
            strokeWidth={2.4}
            strokeLinecap="round"
          />
          <Path d="M132 91L124 85V97L132 91Z" fill={mauve} />
        </Svg>
      ) : null}

      {kind === "freezing" ? (
        <Svg width={174} height={118} viewBox="0 0 174 118" fill="none">
          <Circle
            cx={86}
            cy={60}
            r={24}
            fill={fill}
            stroke={clay}
            strokeWidth={2}
          />
          <Circle cx={86} cy={60} r={8} stroke={ink} strokeWidth={1.5} />
          {[0, 60, 120].map((rotation) => (
            <G key={rotation} rotation={rotation} origin="86,60">
              <Line
                x1={86}
                y1={25}
                x2={86}
                y2={95}
                stroke={mauve}
                strokeWidth={1.8}
              />
              <Line
                x1={78}
                y1={35}
                x2={86}
                y2={43}
                stroke={mauve}
                strokeWidth={1.5}
              />
              <Line
                x1={94}
                y1={35}
                x2={86}
                y2={43}
                stroke={mauve}
                strokeWidth={1.5}
              />
            </G>
          ))}
          <Polygon
            points="126,36 135,43 132,55 120,55 117,43"
            fill={fill}
            stroke={softInk}
          />
        </Svg>
      ) : null}

      {kind === "maturity" ? (
        <Svg width={174} height={118} viewBox="0 0 174 118" fill="none">
          <Ellipse cx={84} cy={64} rx={56} ry={36} fill={clayWash} />
          {[
            { cx: 54, cy: 67, r: 15, inner: 4, strong: false },
            { cx: 78, cy: 47, r: 18, inner: 5, strong: true },
            { cx: 103, cy: 70, r: 20, inner: 7, strong: true },
            { cx: 124, cy: 50, r: 12, inner: 3, strong: false },
          ].map((item) => (
            <G key={`${item.cx}-${item.cy}`}>
              <Circle
                cx={item.cx}
                cy={item.cy}
                r={item.r}
                fill={fill}
                stroke={item.strong ? clay : softInk}
                strokeWidth={item.strong ? 2.2 : 1.5}
              />
              <Circle
                cx={item.cx}
                cy={item.cy}
                r={item.inner}
                fill={item.strong ? "rgba(230,177,144,0.34)" : "transparent"}
                stroke={ink}
                strokeWidth={1.4}
              />
            </G>
          ))}
          <Path
            d="M45 29C64 18 109 18 130 30"
            stroke={softInk}
            strokeWidth={1.8}
            strokeLinecap="round"
          />
        </Svg>
      ) : null}

      {kind === "attrition" ? (
        <Svg width={174} height={118} viewBox="0 0 174 118" fill="none">
          {[28, 54, 80].map((cx) => (
            <Circle
              key={cx}
              cx={cx}
              cy={38}
              r={8}
              fill={fill}
              stroke={clay}
              strokeWidth={1.5}
            />
          ))}
          {[43, 69].map((cx) => (
            <Circle
              key={cx}
              cx={cx}
              cy={72}
              r={11}
              fill={fill}
              stroke={mauve}
              strokeWidth={1.7}
            />
          ))}
          <Circle
            cx={118}
            cy={60}
            r={20}
            fill={fill}
            stroke={apricot}
            strokeWidth={2}
          />
          <Circle cx={112} cy={55} r={5} fill="rgba(74,48,44,0.24)" />
          <Circle cx={124} cy={62} r={5} fill="rgba(74,48,44,0.24)" />
          <Path
            d="M87 39C97 39 103 48 109 54"
            stroke={softInk}
            strokeWidth={1.8}
            strokeLinecap="round"
          />
          <Path
            d="M79 72C92 72 100 67 109 62"
            stroke={softInk}
            strokeWidth={1.8}
            strokeLinecap="round"
          />
        </Svg>
      ) : null}
    </View>
  );
}

function DetailModal({
  slide,
  onClose,
}: {
  slide: BiologySlide | null;
  onClose: () => void;
}) {
  const t = useTranslations();
  const capturePress = usePressCapture("DetailModal");
  return (
    <Modal
      visible={!!slide}
      transparent
      animationType="fade"
      onRequestClose={onClose}
    >
      <Pressable
        onPress={onClose}
        style={{
          flex: 1,
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: "rgba(0,0,0,0.42)",
          padding: 18,
        }}
      >
        <Pressable
          onPress={(e) => e.stopPropagation()}
          style={{
            width: "100%",
            maxWidth: 420,
            maxHeight: "78%",
            borderRadius: 22,
            overflow: "hidden",
            backgroundColor: "rgba(255,255,255,0.92)",
            ...(Platform.OS === "web"
              ? {
                  backdropFilter: "blur(22px) saturate(1.2)",
                  WebkitBackdropFilter: "blur(22px) saturate(1.2)",
                }
              : null),
          }}
        >
          <View
            style={{
              flexDirection: "row",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 12,
              padding: 20,
              borderBottomWidth: 1,
              borderBottomColor: "rgba(74, 51, 39, 0.08)",
            }}
          >
            <View style={{ flex: 1 }}>
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 11,
                  lineHeight: 14,
                  color: EMBER.colors.textLight,
                  fontWeight: "700",
                  letterSpacing: 0.5,
                  textTransform: "uppercase",
                }}
              >
                {t("homepage.egg_freezing_biology_carousel.biology_detail")}
              </Text>
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 20,
                  lineHeight: 25,
                  color: EMBER.colors.text,
                  fontWeight: "700",
                  marginTop: 4,
                }}
              >
                {slide?.detail.title}
              </Text>
            </View>
            <Pressable
              onPress={() => {
                capturePress(pressLabel("close_biology_detail"));
                onClose();
              }}
              hitSlop={10}
            >
              <X size={20} color={EMBER.colors.textLight} strokeWidth={1.8} />
            </Pressable>
          </View>

          <ScrollView
            showsVerticalScrollIndicator={false}
            contentContainerStyle={{ padding: 20, paddingBottom: 24 }}
          >
            {slide?.detail.sections.map((section, index) => (
              <View
                key={section.title}
                style={{ marginTop: index === 0 ? 0 : 18 }}
              >
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 14,
                    lineHeight: 18,
                    color: EMBER.colors.text,
                    fontWeight: "700",
                  }}
                >
                  {section.title}
                </Text>
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 13,
                    lineHeight: 20,
                    color: EMBER.colors.textMid,
                    fontWeight: "400",
                    marginTop: 5,
                  }}
                >
                  {section.body}
                </Text>
              </View>
            ))}
          </ScrollView>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

function BiologyCard({
  slide,
  width,
  onOpen,
}: {
  slide: BiologySlide;
  width: number;
  onOpen: () => void;
}) {
  const t = useTranslations();
  const capturePress = usePressCapture("BiologyCard");
  return (
    <View
      style={{
        width,
        minHeight: 342,
        borderRadius: 18,
        borderWidth: 1,
        borderColor: "rgba(92, 52, 32, 0.12)",
        backgroundColor: "rgba(255,255,255,0.62)",
        padding: 15,
        overflow: "hidden",
      }}
    >
      <View
        style={{
          position: "absolute",
          right: -24,
          top: -22,
          width: 118,
          height: 118,
          borderRadius: 999,
          backgroundColor: "rgba(166, 90, 50, 0.08)",
          borderWidth: 1,
          borderColor: "rgba(166, 90, 50, 0.12)",
        }}
      />
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 10,
        }}
      >
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 10,
            lineHeight: 13,
            color: EMBER.colors.textLight,
            fontWeight: "700",
            letterSpacing: 0.5,
            textTransform: "uppercase",
          }}
        >
          {slide.label}
        </Text>
        <Pressable
          onPress={() => {
            capturePress(
              pressLabel(
                t("homepage.egg_freezing_biology_carousel.open_detail_for", {
                  params: { title: slide.title },
                }),
              ),
              slide.id,
            );
            onOpen();
          }}
          hitSlop={8}
          accessibilityRole="button"
          accessibilityLabel={t(
            "homepage.egg_freezing_biology_carousel.open_detail_for",
            { params: { title: slide.title } },
          )}
          style={({ pressed }) => ({
            width: 30,
            height: 30,
            borderRadius: 999,
            alignItems: "center",
            justifyContent: "center",
            borderWidth: 1,
            borderColor: "rgba(74, 51, 39, 0.12)",
            backgroundColor: pressed
              ? "rgba(74, 51, 39, 0.08)"
              : "rgba(255,255,255,0.54)",
          })}
        >
          <Eye size={15} color={EMBER.colors.text} strokeWidth={1.7} />
        </Pressable>
      </View>

      <BiologyIllustration kind={slide.visual} />

      <Text
        className="font-dm-sans"
        style={{
          fontSize: 17,
          lineHeight: 21,
          color: EMBER.colors.text,
          fontWeight: "700",
        }}
      >
        {slide.title}
      </Text>
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 13,
          lineHeight: 19,
          color: EMBER.colors.textMid,
          fontWeight: "400",
          marginTop: 8,
        }}
      >
        {slide.body}
      </Text>

      <View
        style={{
          marginTop: 12,
          borderRadius: 12,
          borderWidth: 1,
          borderColor: "rgba(94, 110, 62, 0.12)",
          backgroundColor: "rgba(94, 110, 62, 0.07)",
          padding: 10,
        }}
      >
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 11,
            lineHeight: 15,
            color: EMBER.colors.text,
            fontWeight: "700",
          }}
        >
          {t("homepage.egg_freezing_biology_carousel.reserve_note")}
        </Text>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 12,
            lineHeight: 17,
            color: EMBER.colors.textMid,
            fontWeight: "400",
            marginTop: 3,
          }}
        >
          {slide.reserveNote}
        </Text>
      </View>
    </View>
  );
}

export function EggFreezingBiologyCarouselCard() {
  const t = useTranslations();
  const slides = useBiologySlides();
  const [selectedSlide, setSelectedSlide] = useState<BiologySlide | null>(null);

  return (
    <View style={{ marginTop: 16 }}>
      <View style={{ marginBottom: 12, paddingHorizontal: 2 }}>
        <SectionTitle>
          {t("homepage.egg_freezing_biology_carousel.how_it_works")}
        </SectionTitle>
      </View>
      <View
        style={{
          ...(GLASS_CARD_STYLE as Record<string, unknown>),
          borderRadius: 20,
          padding: 16,
          overflow: "hidden",
        }}
      >
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 14,
            lineHeight: 20,
            color: EMBER.colors.textMid,
            fontWeight: "400",
            marginBottom: 14,
          }}
        >
          {t("homepage.egg_freezing_biology_carousel.biology_first_intro")}
        </Text>

        <HorizontalSnapCarousel
          items={slides}
          gap={10}
          fallbackCardWidth={244}
          getCardWidth={(containerWidth) =>
            Math.min(262, Math.max(226, containerWidth * 0.76))
          }
          keyExtractor={(slide) => slide.id}
          renderItem={(slide, cardWidth) => (
            <BiologyCard
              slide={slide}
              width={cardWidth}
              onOpen={() => setSelectedSlide(slide)}
            />
          )}
          contentContainerStyle={{ paddingRight: 4 }}
        />
      </View>

      <DetailModal
        slide={selectedSlide}
        onClose={() => setSelectedSlide(null)}
      />
    </View>
  );
}
